import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { array, boolean, date, lazy, mixed, number, object, string } from "yup";
import { Pageable } from "../../../services/pageable";
import { AllowedBlockchains } from "../../chain/models/internal";
import { countries } from "../../common/models";
import { Field } from "./internal/field";

/**
 * Internal Models to use in the forms. The DTOs do not match the UI/UX, so their types are
 * very cumbersome to use in the UX/UI. Use the DTO's mapper function to map from this type
 * to DTO.
 */
export namespace LaunchConfigurationsDTO {
  export namespace Between {
    export interface T {
      start: Date;
      end: Date;
    }
    export const schema = object({
      start: date().required().label("Start date"),
      end: date()
        .required()
        .test(
          "invalid-date",
          "End date must be after start date",
          (value, context) =>
            value === undefined ||
            context.parent.start === undefined ||
            moment(context.parent.start).isBefore(moment(value))
        )
        .label("End date"),
    }).required();
  }

  export namespace PledgingInfo {
    export interface T {
      amount: {
        minimum: number;
        maximum: number;
      };
      decimalPoints: number;
      amountExceededPoaRequirement?: null | number;
    }
    export const schema = object().shape({
      amount: object().shape({
        minimum: number().required().label("Minimum"),
        maximum: number().required().label("Maximum"),
      }),
      decimalPoints: number().required().label("Decimal Points"),
    });
  }

  export namespace Chain {
    export type T = { token: string; info: PledgingInfo.T };
    export const schema = object().shape({
      token: string().required().label("Token"),
      info: PledgingInfo.schema,
    });
  }

  export namespace Requests {
    export namespace SetSingle {
      export type T = Omit<
        Responses.GetSingle.T,
        "id" | "status" | "createdDate" | "state"
      >;

      export const useSchema = () => {
        return useMemo(
          () =>
            object().shape({
              pages: array()
                .of(
                  object()
                    .shape({
                      title: mixed().required(),
                      fields: array().of(mixed()).required(),
                    })
                    .required()
                )
                .required(),
              configuration: object().shape({
                between: Between.schema,
                consentNode: string(),
                cryptoChains: lazy((val) => {
                  // TODO remove;
                  return mixed();
                }),
                css: object().shape({
                  headerBackgroundColor: object().shape({
                    degree: number(),
                    start: string(),
                    startAlpha: number(),
                    end: string(),
                    endAlpha: number(),
                  }),
                  headerTextColor: string(),
                  formBackgroundColor: string(),
                  textColor: string(),
                  buttonPrimaryColor: string(),
                  buttonSecondaryColor: string(),
                }),
                faceAttachmentRequired: boolean(),
                formTitle: mixed().when(
                  "supportedFormLanguages",
                  (langs: string[]) =>
                    object().shape(
                      langs
                        ? langs.reduce(
                          (acc, curr) => ({
                            ...acc,
                            [curr]: string().required(
                              "This is a required field"
                            ),
                          }),
                          {}
                        )
                        : {}
                    )
                ),
                header: object().shape({
                  imageUrl: string().nullable(),
                  text: mixed().when(
                    "supportedFormLanguages",
                    (langs: string[]) =>
                      object().shape(
                        langs
                          ? langs.reduce(
                            (acc, curr) => ({
                              ...acc,
                              [curr]: string().required(
                                "This is a required field"
                              ),
                            }),
                            {}
                          )
                          : {}
                      )
                  ),
                }),
                launchName: mixed().when(
                  "supportedFormLanguages",
                  (langs: string[]) =>
                    object().shape(
                      langs
                        ? langs.reduce(
                          (acc, curr) => ({
                            ...acc,
                            [curr]: string().required(
                              "This is a required field"
                            ),
                          }),
                          {}
                        )
                        : {}
                    )
                ),
                merchantConfig: object().shape({
                  merchantId: string(),
                  preVerifiedSourceWallet: boolean(),
                  createUserProfile: boolean(),
                  performBackgroundChecks: boolean(),
                  performOtp: boolean(),
                  shouldUseShortFlow: boolean(),
                }),
                participationServices: object().shape({
                  messageSendingWhenVerified: boolean(),
                  onchainAnalysis: object().shape({
                    enabled: boolean(),
                    maxThreshold: number(),
                    realtime: boolean(),
                  }),
                }),
                poaRequiredCountries: array().of(
                  mixed().oneOf(["ALL", ...countries.keys])
                ),
                prohibitedCountries: array().of(mixed().oneOf(countries.keys)),
                referrer: string(),
                supportedFormLanguages: array().min(1).of(string()).required(),
                welcomeMessage: mixed().when(
                  "supportedFormLanguages",
                  (langs: string[]) =>
                    object().shape(
                      langs
                        ? langs.reduce(
                          (acc, curr) => ({
                            ...acc,
                            [curr]: string().required(
                              "This is a required field"
                            ),
                          }),
                          {}
                        )
                        : {}
                    )
                ),
                submitMessage: mixed().when(
                  "supportedFormLanguages",
                  (langs: string[]) =>
                    object().shape(
                      langs
                        ? langs.reduce(
                          (acc, curr) => ({
                            ...acc,
                            [curr]: string().required(
                              "This is a required field"
                            ),
                          }),
                          {}
                        )
                        : {}
                    )
                ),
                whitelistedIPs: array().of(string()),
                allowMultipleSubmissions: boolean(),
              }),
            }),
          [Between.schema]
        );
      };

      export const defaults: Partial<T> = {};
    }

    export namespace AddImage {
      export interface T {
        headerImage: File;
      }
      export const useSchema = () => {
        const { t } = useTranslation("fields");

        return useMemo(
          () =>
            mixed()
              .required(
                t("required-field", {
                  ns: "errors",
                  field: t("identity-document-file"),
                })
              )
              .test(
                "is-large",
                t(`file-size-cannot-exceed-6-mb`),
                (file: any) =>
                  !(file instanceof File) || file.size <= 6 * 1024 * 1024
              ),
          [t]
        );
      };
    }
  }

  export namespace Responses {
    export namespace GetSingle {
      export interface T {
        id: string;
        state: string;
        pages: Array<{
          title: Record<string, string>;
          fields: Array<Field.Field<string>>;
        }>;
        configuration: {
          between?: Between.T;
          consentNote: string | null;
          cryptoChains: Partial<Record<
            AllowedBlockchains,
            {
              pledgingInfo: Record<string, PledgingInfo.T>;
            }
          > | null>;
          css: {
            headerBackgroundColor: {
              degree: number;
              start: string;
              startAlpha: number;
              end: string;
              endAlpha: number;
            };
            headerTextColor: string;
            formBackgroundColor: string;
            textColor: string;
            buttonPrimaryColor: string;
            buttonSecondaryColor: string;
          };
          faceAttachmentRequired: boolean;
          formTitle: Record<string, string>;
          header: {
            imageUrl: string | null;
            text: Record<string, string>;
          };
          launchName: Record<string, string>;
          merchantConfig: {
            merchantId: string;
            preVerifiedSourceWallet: boolean;
            createUserProfile: boolean;
            performBackgroundChecks: boolean;
            performOtp: boolean;
            shouldUseShortFlow?: boolean;
          };
          participationServices: {
            messageSendingWhenVerified: boolean;
            onchainAnalysis: {
              enabled: boolean;
              maxThreshold: number;
              realtime: boolean;
            };
          };
          poaRequiredCountries: Array<"ALL" | typeof countries.T>;
          prohibitedCountries: Array<typeof countries.T>;
          referrer: string;
          supportedFormLanguages: Array<string>;
          welcomeMessage?: Record<string, string>;
          whitelistedIPs: Array<string> | null;
          allowMultipleSubmissions?: boolean;
          submitMessage?: Record<string, string>;
        };
      }
    }
    export namespace GetAll {
      export interface T {
        content: GetSingle.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }

    export namespace AddImage {
      export type T = any;
    }
  }
}
